'use client'

/**
 * The new-school sign-up at `/start`: state and rules of the Encuentro form
 * (components/encuentro/screens/school-signup/start-screen.tsx), ALU-118.
 *
 * - The draft survives a reload (localStorage), except when a signed-in
 *   person's email or a prospect link seeds the fields.
 * - The URL follows the organization's name until the person edits it, and
 *   its availability is checked as they type (`checkSlugAvailability`, the
 *   by-slug branding RPC: a signed-out visitor cannot read `organizations`,
 *   ALU-121).
 * - Submitting calls `requestOrgCreation` with the same five fields. Either
 *   the network is created on the spot (signed in: to the admin setup) or a
 *   confirmation link is emailed, with a 30 s cooldown to send it again.
 *
 * The server actions and the toasts' text are passed in: the Encuentro tree
 * may only import app/actions/*, so its page hands the actions down.
 */
import { useEffect, useRef, useState, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { z } from 'zod'
import { normalizeSlug, slugFromName, typingSlug } from '@/lib/org/reserved-slugs'
import type { StartInstitutionProfile } from '@/lib/schemas/start-institution-profile'
import type { StartErrorCode } from '@/lib/start/start-error-codes'

export type StartField = 'name' | 'slug' | 'email' | 'fullName' | 'attested' | 'general'
export type StartFieldErrors = Partial<Record<StartField, string>>
export type StartSlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'reserved' | 'invalid'

/** What `requestOrgCreation` (app/start/actions.ts) returns. */
export type RequestOrgCreationResult =
  | { success: true; checkEmail: true; email: string }
  | { success: true; immediate: true; orgSlug: string; reviewLogo?: true }
  | {
      success: false
      error: string
      /** Stable code of `error` (lib/start/start-error-codes.ts). */
      code?: StartErrorCode
      field?: StartField
      retryAfter?: number
    }

export type StartFormActions = {
  requestOrgCreation: (formData: FormData) => Promise<RequestOrgCreationResult>
  checkSlugAvailability: (
    slug: string
  ) => Promise<{ available: boolean; reason?: 'reserved' | 'taken' | 'invalid' }>
}

const DRAFT_KEY = 'alumnia_start_form_draft'
export const RESEND_COOLDOWN_SECONDS = 30

const draftSchema = z.object({
  name: z.string().max(80).optional(),
  slug: z.string().max(40).optional(),
  slugTouched: z.boolean().optional(),
  email: z.string().max(254).optional(),
  fullName: z.string().max(80).optional(),
  attested: z.boolean().optional(),
})
type Draft = z.infer<typeof draftSchema>

function readDraft(): Draft {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY)
    if (!raw) return {}
    const parsed = draftSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : {}
  } catch {
    return {}
  }
}

function writeDraft(d: Draft) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(d))
  } catch {
    // Quota or privacy mode — silent fail.
  }
}

function clearDraft() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(DRAFT_KEY)
  } catch {
    // ignore
  }
}

export function useStartForm({
  initialEmail = '',
  initialName = '',
  initialSlug = '',
  actions,
  text,
  institutionProfile = null,
}: {
  initialEmail?: string
  initialName?: string
  initialSlug?: string
  actions: StartFormActions
  text: {
    networkReady: string
    newLinkSent: string
    /** The form's own wording per error code; a code not listed keeps the action's English. */
    errors?: Partial<Record<StartErrorCode, string>>
  }
  institutionProfile?: StartInstitutionProfile | null
}) {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [name, setName] = useState(initialName)
  const [slug, setSlug] = useState(initialSlug)
  // Treat a prefilled slug as user-set so typing the name doesn't overwrite it.
  const [slugTouched, setSlugTouched] = useState(!!initialSlug)
  const [email, setEmail] = useState(initialEmail)
  const [fullName, setFullName] = useState('')
  const [attested, setAttested] = useState(false)
  const [errors, setErrors] = useState<StartFieldErrors>({})
  const [submitted, setSubmitted] = useState<{ email: string } | null>(null)
  const [resendIn, setResendIn] = useState(0)
  const [isResending, setIsResending] = useState(false)
  const draftLoadedRef = useRef(false)
  const [slugStatus, setSlugStatus] = useState<StartSlugStatus>('idle')

  /* eslint-disable react-hooks/set-state-in-effect --
     One-shot restore from localStorage on mount. setState in effect is the
     correct pattern when hydrating client-only state that the server can't
     render. */
  useEffect(() => {
    const d = readDraft()
    if (!initialName && d.name) setName(d.name)
    if (!initialSlug && d.slug) setSlug(d.slug)
    if (!initialSlug && d.slugTouched) setSlugTouched(true)
    if (initialEmail) setEmail(initialEmail)
    else if (d.email) setEmail(d.email)
    if (d.fullName) setFullName(d.fullName)
    if (!initialName && !initialSlug && !initialEmail && d.attested) setAttested(true)
    draftLoadedRef.current = true
  }, [initialEmail, initialName, initialSlug])
  /* eslint-enable react-hooks/set-state-in-effect */

  // Persist draft to localStorage on every change after the initial restore.
  useEffect(() => {
    if (!draftLoadedRef.current) return
    writeDraft({ name, slug, slugTouched, email, fullName, attested })
  }, [name, slug, slugTouched, email, fullName, attested])

  const { checkSlugAvailability } = actions
  /* eslint-disable react-hooks/set-state-in-effect --
     Debounced live availability check against an external system (database).
     setState inside the effect is the correct pattern for async debounce. */
  useEffect(() => {
    const finalSlug = normalizeSlug(slug)
    if (!finalSlug || finalSlug.length < 3) {
      setSlugStatus('idle')
      return
    }
    setSlugStatus('checking')
    let cancelled = false
    const timer = setTimeout(async () => {
      const result = await checkSlugAvailability(finalSlug).catch(() => null)
      if (cancelled) return
      if (!result) {
        setSlugStatus('idle')
        return
      }
      if (result.available) setSlugStatus('available')
      else if (result.reason === 'taken') setSlugStatus('taken')
      else if (result.reason === 'reserved') setSlugStatus('reserved')
      else setSlugStatus('invalid')
    }, 350)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [slug, checkSlugAvailability])
  /* eslint-enable react-hooks/set-state-in-effect */

  // Resend countdown — ticks every second while > 0.
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  function handleNameChange(next: string) {
    setName(next)
    if (!slugTouched) {
      setSlug(slugFromName(next).slice(0, 40))
    }
  }

  function handleSlugChange(next: string) {
    setSlugTouched(true)
    setSlug(typingSlug(next).slice(0, 40))
  }

  function errorText(result: { error: string; code?: StartErrorCode }): string {
    return (result.code && text.errors?.[result.code]) || result.error
  }

  function buildFormData(): FormData {
    const fd = new FormData()
    fd.set('name', name)
    fd.set('slug', slug)
    fd.set('email', email)
    fd.set('fullName', fullName)
    fd.set('attested', attested ? 'true' : 'false')
    if (institutionProfile) fd.set('institutionProfile', JSON.stringify(institutionProfile))
    return fd
  }

  function handleSubmit(formData: FormData) {
    if (institutionProfile) formData.set('institutionProfile', JSON.stringify(institutionProfile))
    setErrors({})
    startTransition(async () => {
      const result = await actions.requestOrgCreation(formData)
      if (!result.success) {
        const field = result.field || 'general'
        const message = errorText(result)
        setErrors({ [field]: message })
        if (field === 'general') toast.error(message)
        return
      }
      if ('immediate' in result) {
        clearDraft()
        toast.success(text.networkReady)
        router.push(
          `/in/${result.orgSlug}/admin/setup?welcome=1${result.reviewLogo ? '&reviewLogo=1' : ''}`
        )
        return
      }
      setSubmitted({ email: result.email })
      setResendIn(RESEND_COOLDOWN_SECONDS)
    })
  }

  function handleResend() {
    if (resendIn > 0 || isResending) return
    setIsResending(true)
    void (async () => {
      const result = await actions.requestOrgCreation(buildFormData())
      setIsResending(false)
      if (!result.success) {
        toast.error(errorText(result))
        return
      }
      if ('checkEmail' in result) {
        toast.success(text.newLinkSent)
        setResendIn(RESEND_COOLDOWN_SECONDS)
      }
    })()
  }

  /** "Edit and try again" from the check-your-email state. */
  function editAgain() {
    setSubmitted(null)
    setResendIn(0)
  }

  /** The submit button waits for the box and for a URL that is not known to be taken. */
  const canSubmit =
    !isPending &&
    attested &&
    slugStatus !== 'taken' &&
    slugStatus !== 'reserved' &&
    slugStatus !== 'checking'

  return {
    name,
    slug,
    email,
    setEmail,
    fullName,
    setFullName,
    attested,
    setAttested,
    errors,
    submitted,
    resendIn,
    isResending,
    isPending,
    slugStatus,
    canSubmit,
    handleNameChange,
    handleSlugChange,
    buildFormData,
    handleSubmit,
    handleResend,
    editAgain,
  }
}

export type StartFormState = ReturnType<typeof useStartForm>
